import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";

import { useAuth } from "@bw/shared/hooks";
import { isApiError } from "@bw/shared/api";
import {
  PRELAUNCH_PRIVACY_ACKNOWLEDGEMENT,
  PRELAUNCH_TERMS_ACKNOWLEDGEMENT,
} from "@bw/shared";

import {
  StructuredAddressSection,
  createAddressDraft,
  isAddressDraftVerified,
  type AddressDraft,
} from "../address/StructuredAddressSection";
import { compactTaxId, isTaxIdReady, TAX_ID_HELPER_TEXT } from "./taxId";

// ---------------------------------------------------------------------------
// F3b — the RICH create-business wizard.
//
// Replaces the thin flat form with the original 3-section ACCORDION wizard
// (ported from deprecated-business@16fd6b71 `BusinessRegistrationForm`) rebuilt
// with MUI + F3a's `StructuredAddressSection` for the HQ address.
//
// The wizard is a THIN presentation layer: it collects fields, gates submit on
// PRESENCE/format (form UX, not business logic), and calls `onSubmit(values)`
// with the collected payload. The backend (OrgRegistration) remains the single
// authority on what a valid org is. The headline drift fix: the payload now
// INCLUDES the verified latitude/longitude (the thin form dropped them).
//
// CONTRACT KEPT: `CreateBusinessFormProps` still exposes `onSubmit(values)`,
// `error`, and `submitting` so getting-started.tsx's onCreateBusiness slot keeps
// working unchanged.
// ---------------------------------------------------------------------------

const STORAGE_KEY_PREFIX = "bw-registration-draft";
const DRAFT_VERSION = 2;

// The payload handed to `onSubmit`. This is the shape getting-started.tsx maps
// onto the backend OrgRegistration. It is intentionally permissive (the backend
// validates); the FE only forwards collected organization fields. The policy
// checkboxes acknowledge nonbinding prelaunch drafts and are intentionally not
// represented as legal-version acceptances in this payload.
export interface CreateBusinessFormValues {
  business_name: string;
  legal_name: string;
  country: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  tax_id?: string;
  latitude?: number | null;
  longitude?: number | null;
  full_name: string;
  owner_full_name: string;
  owner_email: string;
  phone: string;
  owner_phone: string;
  business_categories: string[];
}

export interface CreateBusinessFormProps {
  onSubmit: (values: CreateBusinessFormValues) => void | Promise<void>;
  error?: string | null;
  submitting?: boolean;
}

interface RegistrationDraft {
  version?: number;
  fullName?: string;
  businessName?: string;
  legalName?: string;
  phone?: string;
  taxId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: number | null;
  longitude?: number | null;
  source?: "google" | "manual";
  tosAccepted?: boolean;
  privacyAccepted?: boolean;
}

type RegistrationSectionKey = "profile" | "address" | "policies" | "none";

function getDraftStorageKey(userKey: string | null | undefined): string {
  return userKey ? `${STORAGE_KEY_PREFIX}:${userKey}` : STORAGE_KEY_PREFIX;
}

function loadDraft(storageKey: string): RegistrationDraft {
  try {
    const draft = JSON.parse(sessionStorage.getItem(storageKey) || "{}");
    return draft.version === DRAFT_VERSION ? draft : {};
  } catch {
    return {};
  }
}

function addressDraftFromRegistration(draft: RegistrationDraft): AddressDraft {
  return createAddressDraft(
    draft.addressLine1
      ? {
          line1: draft.addressLine1,
          line2: draft.addressLine2 ?? null,
          city: draft.city ?? "",
          state: draft.state ?? "",
          postal_code: draft.postalCode ?? "",
          country: draft.country ?? "US",
          latitude: draft.latitude ?? null,
          longitude: draft.longitude ?? null,
        }
      : null,
    draft.formattedAddress,
  );
}

function PolicyAcceptanceRow({
  checked,
  id,
  label,
  linkHref,
  linkLabel,
  onChange,
}: {
  checked: boolean;
  id: string;
  label: string;
  linkHref: string;
  linkLabel: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ bgcolor: "action.hover", px: 2, py: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              id={id}
              onChange={(event) => onChange(event.target.checked)}
            />
          }
          label={label}
          sx={{ m: 0 }}
        />
        <Link
          href={linkHref}
          rel="noopener noreferrer"
          target="_blank"
          underline="hover"
          variant="body2"
          sx={{ flexShrink: 0, fontWeight: 700 }}
        >
          {linkLabel}
        </Link>
      </Stack>
    </Paper>
  );
}

function RegistrationAccordion({
  sectionKey,
  title,
  status,
  open,
  onOpenChange,
  children,
}: {
  sectionKey: Exclude<RegistrationSectionKey, "none">;
  title: string;
  status: "Complete" | "Needs info" | "Next";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  // Status is conveyed by the Badge variant + label text, not colour alone —
  // mirrors getting-started's StatusPill: Complete -> secondary, Needs info ->
  // destructive (still blocking), Next -> outline (not yet due). This wizard is
  // the pre-org registration flow (no org exists yet to render an onboarding
  // checklist for) and keeps its own accordion; it is intentionally unrelated to
  // the post-registration Getting Started workspace, which is a flat one-page
  // form, not an accordion.
  const statusColor =
    status === "Complete"
      ? "success"
      : status === "Needs info"
        ? "warning"
        : "default";

  return (
    <Accordion
      data-registration-section={sectionKey}
      disableGutters
      elevation={0}
      expanded={open}
      onChange={(_event, expanded) => onOpenChange(expanded)}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: "12px !important",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        aria-controls={`registration-${sectionKey}-content`}
        expandIcon={<ExpandMoreRoundedIcon />}
        id={`registration-${sectionKey}-header`}
        sx={{ px: { xs: 2, sm: 2.5 }, py: 0.5 }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center", flexWrap: "wrap", minWidth: 0 }}
        >
          <Typography
            component="h2"
            variant="subtitle1"
            sx={{ fontWeight: 700 }}
          >
            {title}
          </Typography>
          <Chip
            color={statusColor}
            label={status}
            size="small"
            variant="outlined"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        id={`registration-${sectionKey}-content`}
        sx={{ borderTop: 1, borderColor: "divider", p: { xs: 2, sm: 2.5 } }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export function CreateBusinessForm({
  onSubmit,
  error: externalError,
  submitting: externalSubmitting,
}: CreateBusinessFormProps) {
  const { user } = useAuth();
  const userKey = user?.uid ?? user?.email ?? null;
  const draftStorageKey = getDraftStorageKey(userKey);

  // The initial draft is read once per mounted storage key. We re-read it via
  // an effect when the key changes (a different owner signs in).
  const initialDraft = userKey ? loadDraft(draftStorageKey) : {};

  const [fullName, setFullName] = useState(initialDraft.fullName ?? "");
  const [businessName, setBusinessName] = useState(
    initialDraft.businessName ?? "",
  );
  const [legalName, setLegalName] = useState(initialDraft.legalName ?? "");
  const [phone, setPhone] = useState(initialDraft.phone ?? "");
  const [taxId, setTaxId] = useState(initialDraft.taxId ?? "");
  const [addressDraft, setAddressDraft] = useState<AddressDraft>(() =>
    addressDraftFromRegistration(initialDraft),
  );
  const [tosAccepted, setTosAccepted] = useState(
    Boolean(initialDraft.tosAccepted),
  );
  const [privacyAccepted, setPrivacyAccepted] = useState(
    Boolean(initialDraft.privacyAccepted),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const [loadedDraftStorageKey, setLoadedDraftStorageKey] =
    useState(draftStorageKey);
  const [openSection, setOpenSection] =
    useState<RegistrationSectionKey>("profile");
  // Tracks a completed submit so the draft-save effect stops re-writing a
  // cleared draft.
  const [submitted, setSubmitted] = useState(false);

  const isSubmitting = internalSubmitting || Boolean(externalSubmitting);

  // When the auth uid changes (a different owner), reload that owner's draft
  // (or an empty one) and reset the wizard.
  useEffect(() => {
    if (loadedDraftStorageKey === draftStorageKey) return;
    const nextDraft = userKey ? loadDraft(draftStorageKey) : {};
    setFullName(nextDraft.fullName ?? "");
    setBusinessName(nextDraft.businessName ?? "");
    setLegalName(nextDraft.legalName ?? "");
    setPhone(nextDraft.phone ?? "");
    setTaxId(nextDraft.taxId ?? "");
    setAddressDraft(addressDraftFromRegistration(nextDraft));
    setTosAccepted(Boolean(nextDraft.tosAccepted));
    setPrivacyAccepted(Boolean(nextDraft.privacyAccepted));
    setLoadedDraftStorageKey(draftStorageKey);
    setOpenSection("profile");
    setSubmitted(false);
    setSubmitError(null);
  }, [draftStorageKey, loadedDraftStorageKey, userKey]);

  const saveDraft = useCallback(() => {
    sessionStorage.setItem(
      draftStorageKey,
      JSON.stringify({
        version: DRAFT_VERSION,
        fullName,
        businessName,
        legalName,
        phone,
        taxId,
        addressLine1: addressDraft.line1,
        addressLine2: addressDraft.line2,
        city: addressDraft.city,
        state: addressDraft.state,
        postalCode: addressDraft.postalCode,
        country: addressDraft.country,
        formattedAddress: addressDraft.formattedAddress,
        latitude: addressDraft.latitude,
        longitude: addressDraft.longitude,
        source: addressDraft.source,
        tosAccepted,
        privacyAccepted,
      } satisfies RegistrationDraft),
    );
  }, [
    addressDraft,
    businessName,
    draftStorageKey,
    fullName,
    legalName,
    phone,
    privacyAccepted,
    taxId,
    tosAccepted,
  ]);

  // Persist the per-user draft on every change (once the right draft slot is
  // loaded and the create has not yet succeeded).
  useEffect(() => {
    if (submitted) return;
    if (!userKey) return;
    if (loadedDraftStorageKey !== draftStorageKey) return;
    saveDraft();
  }, [draftStorageKey, loadedDraftStorageKey, saveDraft, submitted, userKey]);

  const taxIdReady = isTaxIdReady(taxId);
  // Absence is allowed by OrgRegistration. A supplied value still uses the
  // strict shared validator; dashes or invisible Unicode are not absence.
  const taxIdOmitted = /^[ \t]*$/.test(taxId);
  const profileReady =
    fullName.trim() !== "" &&
    businessName.trim() !== "" &&
    legalName.trim() !== "" &&
    phone.trim() !== "" &&
    (taxIdOmitted || taxIdReady);
  const addressReady = isAddressDraftVerified(addressDraft);
  const policiesReady = tosAccepted && privacyAccepted;
  const registrationReady = profileReady && addressReady && policiesReady;
  const canSubmit = registrationReady && !isSubmitting;

  const nextMissingSection = !profileReady
    ? "Business Profile"
    : !addressReady
      ? "Business Headquarters"
      : !policiesReady
        ? "Terms & Policies"
        : null;

  // Finishing a supplied EIN retains the existing profile auto-advance. With
  // no EIN, keep the optional field available and let the owner open HQ when
  // ready; do not collapse the profile while they are still typing the phone.
  //
  // The HQ (`address`) section deliberately does NOT auto-advance when it
  // becomes ready: verifying the address (via a Google suggestion or the Verify
  // button) is itself the completion act, and the owner still needs to review
  // the parsed Line 1 / City / State / ZIP AND add the optional Address Line 2
  // (suite/unit). Collapsing it the moment a suggestion is picked steals focus
  // before they can do either. The owner advances out of HQ explicitly by
  // clicking the next section header. The "Complete" pill still flips — we just
  // don't collapse/jump. Only advance forward from the currently-open section;
  // the owner can still manually toggle any section.
  useEffect(() => {
    setOpenSection((current) => {
      if (current === "profile" && profileReady && taxIdReady) {
        if (!addressReady) return "address";
        return policiesReady ? "none" : "policies";
      }
      return current;
    });
  }, [addressReady, policiesReady, profileReady, taxIdReady]);

  const errorRef = useRef<HTMLDivElement | null>(null);
  const error = submitError ?? externalError ?? null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!registrationReady || isSubmitting) return;

    setSubmitError(null);
    setInternalSubmitting(true);
    try {
      const values: CreateBusinessFormValues = {
        business_name: businessName.trim(),
        legal_name: legalName.trim(),
        country: "US",
        address_line1: addressDraft.line1.trim(),
        address_line2: addressDraft.line2.trim() || undefined,
        city: addressDraft.city.trim(),
        state: addressDraft.state.trim(),
        postal_code: addressDraft.postalCode.trim(),
        ...(taxIdOmitted ? {} : { tax_id: compactTaxId(taxId) }),
        latitude: addressDraft.latitude,
        longitude: addressDraft.longitude,
        full_name: fullName.trim(),
        owner_full_name: fullName.trim(),
        owner_email: user?.email ?? "",
        phone: phone.trim(),
        owner_phone: phone.trim(),
        business_categories: [],
      };

      await onSubmit(values);

      // Success — clear the draft and stop re-saving it. The parent owns
      // navigation; the form stays mounted until it unmounts us.
      setSubmitted(true);
      sessionStorage.removeItem(draftStorageKey);
    } catch (err) {
      // Keep the form mounted and surface an honest error. A 409 means the
      // owner already has a pending application.
      if (isApiError(err) && err.status === 409) {
        setSubmitError(
          err.message || "You already have a pending application.",
        );
      } else if (err instanceof Error) {
        setSubmitError(
          err.message || "Something went wrong. Please try again.",
        );
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } finally {
      setInternalSubmitting(false);
    }
  };

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 } }}>
      <Box>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
          Create your business
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
          Register your business to start setting up the platform. Our team
          reviews each application before you go live.
        </Typography>
      </Box>

      {error ? (
        <Alert
          aria-live="assertive"
          ref={errorRef}
          role="alert"
          severity="error"
        >
          {error}
        </Alert>
      ) : null}

      <Stack
        component="form"
        spacing={2}
        onSubmit={handleSubmit}
        noValidate
        sx={{ maxWidth: 800 }}
      >
        <RegistrationAccordion
          sectionKey="profile"
          title="Business Profile"
          status={profileReady ? "Complete" : "Needs info"}
          open={openSection === "profile"}
          onOpenChange={(open) => setOpenSection(open ? "profile" : "none")}
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            }}
          >
            <TextField
              id="reg-full-name"
              label="Your Name"
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Full name"
              value={fullName}
            />
            <TextField
              helperText="From your signed-in account."
              id="reg-email"
              label="Email"
              slotProps={{
                htmlInput: { readOnly: true, "aria-readonly": "true" },
              }}
              type="email"
              value={user?.email ?? ""}
            />
            <TextField
              id="reg-business-name"
              label="Business Name"
              onChange={(event) => setBusinessName(event.target.value)}
              placeholder="Business name"
              value={businessName}
            />
            <TextField
              id="reg-legal-name"
              label="Legal Name"
              onChange={(event) => setLegalName(event.target.value)}
              placeholder="Legal entity name"
              value={legalName}
            />
            <TextField
              id="reg-phone"
              label="Business Phone"
              onChange={(event) => setPhone(event.target.value)}
              placeholder="(555) 555-5555"
              type="tel"
              value={phone}
            />
            <TextField
              helperText={`Optional for business registration. ${TAX_ID_HELPER_TEXT}`}
              error={!taxIdOmitted && !taxIdReady}
              id="reg-tax-id"
              label="Tax ID / EIN (optional)"
              onChange={(event) => setTaxId(event.target.value)}
              placeholder="12-3456789"
              value={taxId}
            />
          </Box>
        </RegistrationAccordion>

        <RegistrationAccordion
          sectionKey="address"
          title="Business Headquarters"
          status={
            addressReady ? "Complete" : profileReady ? "Needs info" : "Next"
          }
          open={openSection === "address"}
          onOpenChange={(open) => setOpenSection(open ? "address" : "none")}
        >
          <StructuredAddressSection
            value={addressDraft}
            onChange={setAddressDraft}
            idPrefix="business-registration-address"
            autocompleteLabel="Headquarters Address"
            autocompletePlaceholder="Search the business address"
            manualLine1Label="Address Line 1"
            helperText="Verify the headquarters address so the platform stores a matched legal location and coordinates."
          />
        </RegistrationAccordion>

        <RegistrationAccordion
          sectionKey="policies"
          title="Terms & Policies"
          status={
            policiesReady ? "Complete" : addressReady ? "Needs info" : "Next"
          }
          open={openSection === "policies"}
          onOpenChange={(open) => setOpenSection(open ? "policies" : "none")}
        >
          <Stack spacing={1.5}>
            <PolicyAcceptanceRow
              id="reg-tos"
              label={PRELAUNCH_TERMS_ACKNOWLEDGEMENT}
              checked={tosAccepted}
              onChange={setTosAccepted}
              linkHref="/terms"
              linkLabel="Read Terms"
            />
            <PolicyAcceptanceRow
              id="reg-privacy"
              label={PRELAUNCH_PRIVACY_ACKNOWLEDGEMENT}
              checked={privacyAccepted}
              onChange={setPrivacyAccepted}
              linkHref="/privacy"
              linkLabel="Read Privacy"
            />
          </Stack>
        </RegistrationAccordion>

        <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Typography color="text.secondary" variant="body2">
              {registrationReady
                ? "Ready to create the business account."
                : `Next: complete ${nextMissingSection}.`}
            </Typography>
            <Button
              disabled={!canSubmit}
              fullWidth
              size="small"
              sx={{ width: { sm: "auto" } }}
              type="submit"
              variant="contained"
            >
              {isSubmitting ? "Creating…" : "Create Business Account"}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}
